import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Megaphone, Send, Mail, MessageCircle, Loader2, Users, History, Sparkles } from "lucide-react";

const QUICK_TEMPLATES = [
  {
    id: "zero-fees",
    label: "Zero platform fees 🎉",
    target: "owners",
    subject: "Big news: ZERO platform fees on TheHostelNow 🎉",
    body: `Namaste! We have completely removed the ₹50/bed/night platform fee on TheHostelNow.com.

✅ Students now pay EXACTLY your listed price — no booking fee, no convenience fee, nothing extra
✅ You keep 100% of your room + meal revenue
✅ Lower effective prices for students = more bookings for you

This is the perfect time to add more rooms, set your room types, and pin your map location so students can find you faster.

Log in to your Owner Dashboard: https://thehostelnow.com/owner

— Team TheHostelNow.com
Verified Stays. No Hidden Charges. Ever.`,
  },
];

/**
 * Admin-only broadcast composer + audit log.
 * Fires bulk email via Resend and/or bulk WhatsApp via Twilio to a target audience.
 * Sends run in a backend background task; the log below shows recent broadcasts.
 */
export default function AdminBroadcastPanel() {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [email, setEmail] = useState(true);
  const [wa, setWa] = useState(true);
  const [target, setTarget] = useState("owners");
  const [sending, setSending] = useState(false);
  const [logs, setLogs] = useState([]);

  const load = () => api.get("/admin/broadcasts").then((r) => setLogs(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!subject.trim() || subject.trim().length < 2) return toast.error("Please enter a subject line");
    if (!body.trim() || body.trim().length < 5) return toast.error("Message body is too short");
    const channels = [email ? "email" : null, wa ? "whatsapp" : null].filter(Boolean);
    if (channels.length === 0) return toast.error("Pick at least one channel (Email or WhatsApp)");
    const targetLabel = target === "owners" ? "all hostel owners" : target === "students" ? "all students" : "everyone";
    if (!window.confirm(`Send this broadcast to ${targetLabel} via ${channels.join(" + ")}?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/broadcast", { subject, body, channels, target });
      toast.success(`Queued for ${data.recipient_count} recipient${data.recipient_count === 1 ? "" : "s"}. Delivery runs in the background.`);
      setSubject(""); setBody("");
      // Refresh log a couple of times so status flips from `sending` → `sent`
      setTimeout(load, 1500);
      setTimeout(load, 6000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Broadcast failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4">
      <div data-testid="admin-broadcast-panel" className="rounded-2xl border-2 border-jodhpur-300 bg-gradient-to-br from-jodhpur-50 to-white p-4 sm:p-5 space-y-3">
        <div className="flex items-center gap-2">
          <div className="grid place-items-center w-9 h-9 rounded-full bg-jodhpur-700 text-white shadow-sm">
            <Megaphone size={16} />
          </div>
          <div className="min-w-0">
            <div className="font-display font-bold">Broadcast a message</div>
            <div className="text-[11px] text-slate-600">Bulk email (Resend) + WhatsApp (Twilio). Delivery runs in the background.</div>
          </div>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Quick templates</label>
          <div className="mt-1 flex gap-2 flex-wrap">
            {QUICK_TEMPLATES.map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid={`broadcast-template-${t.id}`}
                onClick={() => { setTarget(t.target); setSubject(t.subject); setBody(t.body); toast.success("Template loaded — review and hit send"); }}
                className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border-2 border-turmeric-400 bg-turmeric-50 text-vermilion-700 hover:bg-turmeric-100"
              >
                <Sparkles size={12} /> {t.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Audience</label>
          <div className="mt-1 flex gap-2 flex-wrap">
            {[
              { v: "owners", l: "Hostel owners", icon: Users },
              { v: "students", l: "Students", icon: Users },
              { v: "all", l: "Everyone", icon: Users },
            ].map(({ v, l, icon: Icon }) => (
              <button
                key={v}
                type="button"
                data-testid={`broadcast-target-${v}`}
                onClick={() => setTarget(v)}
                className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border-2 ${target === v ? "border-vermilion-600 bg-vermilion-50 text-vermilion-700" : "border-slate-200 bg-white text-slate-600"}`}
              >
                <Icon size={12} /> {l}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Subject / title</label>
          <Input
            data-testid="broadcast-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="e.g. New feature: 20% higher visibility with Boost"
            maxLength={200}
            className="mt-1 h-11"
          />
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Message body</label>
          <Textarea
            data-testid="broadcast-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            maxLength={5000}
            placeholder="Write in plain English. Line breaks are preserved. Emojis welcome ✅"
            className="mt-1"
          />
          <div className="text-[10px] text-slate-400 mt-1">{body.length}/5000 characters</div>
        </div>

        <div className="flex items-center gap-4 flex-wrap">
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              data-testid="broadcast-channel-email"
              checked={email}
              onChange={(e) => setEmail(e.target.checked)}
              className="w-4 h-4 accent-vermilion-600"
            />
            <Mail size={14} /> Email (Resend)
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              data-testid="broadcast-channel-whatsapp"
              checked={wa}
              onChange={(e) => setWa(e.target.checked)}
              className="w-4 h-4 accent-vermilion-600"
            />
            <MessageCircle size={14} /> WhatsApp (Twilio)
          </label>
        </div>

        <div className="flex justify-end">
          <Button
            data-testid="broadcast-send-btn"
            onClick={send}
            disabled={sending}
            className="bg-jodhpur-700 hover:bg-jodhpur-800 text-white gap-2"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {sending ? "Queuing…" : (target === "owners" ? "Send to all hostel owners" : target === "students" ? "Send to all students" : "Send to everyone")}
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-2 mb-2">
          <History size={16} className="text-slate-500" />
          <div className="font-display font-bold text-sm">Recent broadcasts ({logs.length})</div>
        </div>
        {logs.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">No broadcasts sent yet.</div>
        ) : (
          <div className="space-y-2">
            {logs.map((b) => (
              <div key={b.id} data-testid={`broadcast-row-${b.id}`} className="border border-slate-200 rounded-lg p-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold truncate">{b.subject}</span>
                      <Badge className={b.status === "sent" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>{b.status || "sent"}</Badge>
                      <Badge variant="outline" className="text-[10px]">{b.target}</Badge>
                      {(b.channels || []).map((c) => (
                        <Badge key={c} variant="outline" className="text-[10px] gap-1">
                          {c === "email" ? <Mail size={10} /> : <MessageCircle size={10} />} {c}
                        </Badge>
                      ))}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {new Date(b.sent_at).toLocaleString("en-IN")} · by {b.sent_by} · {b.recipient_count} recipient{b.recipient_count === 1 ? "" : "s"}
                    </div>
                    <div className="mt-1 flex gap-3 text-[11px] text-slate-600 flex-wrap">
                      {(b.channels || []).includes("email") && (
                        <span>Email: <b className="text-emerald-700">{b.email_success}</b> sent · <b className="text-rose-600">{b.email_failed}</b> failed</span>
                      )}
                      {(b.channels || []).includes("whatsapp") && (
                        <span>WhatsApp: <b className="text-emerald-700">{b.whatsapp_success}</b> sent · <b className="text-rose-600">{b.whatsapp_failed}</b> failed{" "}
                          {typeof b.used_template === "boolean" && (
                            <span className="text-slate-500">· {b.used_template ? "via template" : "free-form body"}</span>
                          )}
                        </span>
                      )}
                    </div>
                    {b.last_whatsapp_error && (
                      <div
                        data-testid={`broadcast-wa-error-${b.id}`}
                        className="mt-2 text-[11px] rounded-md bg-rose-50 border border-rose-200 text-rose-800 px-2 py-1.5 break-words"
                      >
                        <b>WhatsApp error:</b> {b.last_whatsapp_error}
                        {String(b.last_whatsapp_error).includes("63016") && (
                          <div className="mt-1 text-rose-700">
                            <b>Fix:</b> Twilio requires an approved Content Template for messages outside the 24-hour window. Approve a "Broadcast" template in Twilio Content Template Builder with two placeholders <span className="font-mono">{"{{1}}"}</span> (subject) and <span className="font-mono">{"{{2}}"}</span> (body), then set <span className="font-mono">TWILIO_TPL_BROADCAST</span> in your backend env to the SID.
                          </div>
                        )}
                        {String(b.last_whatsapp_error).includes("63015") && (
                          <div className="mt-1 text-rose-700">
                            <b>Fix:</b> You're sending from the Twilio <b>sandbox</b> number — it can only reach phones that joined the sandbox. Register your own WhatsApp Business sender (Twilio Console → Messaging → Senders → WhatsApp senders), then update <span className="font-mono">TWILIO_WHATSAPP_FROM</span> to the approved number and set <span className="font-mono">TWILIO_TPL_BROADCAST</span> to an approved template SID.
                          </div>
                        )}
                      </div>
                    )}
                    <details className="mt-2">
                      <summary className="text-[11px] text-slate-500 cursor-pointer">View body</summary>
                      <div className="mt-1 whitespace-pre-wrap text-xs bg-slate-50 border border-slate-200 rounded-md p-2 text-slate-800">{b.body}</div>
                    </details>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
